import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import {
  Card,
  Chip,
  Button,
  Divider,
  useTheme,
  TextField,
  CardHeader,
  Typography,
  CardContent,
  IconButton,
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { EntityLabel, EntityLabelType, EntityLabelScopeType } from 'src/services/utils/utils.types';
import {
  useSearchLabelsQuery,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useDeleteLabelMutation,
} from 'src/services/utils/utils.service';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

// ----------------------------------------------------------------------

interface LabelTypeSectionProps {
  type: EntityLabelType;
  title: string;
  canEdit: boolean;
}

/**
 * Manage the agency's own labels for a single entity type (candidate or company): the seeded system
 * labels are shown read-only, the tenant's own labels can be added, renamed and deleted. Each action
 * is a single request against the tenant-scoped /entity-labels endpoints; the EntityLabels cache tag
 * is invalidated on success so the profile labels cards pick up the change immediately.
 */
function LabelTypeSection({ type, title, canEdit }: LabelTypeSectionProps) {
  const { t } = useTranslate();

  const { currentData, isLoading } = useSearchLabelsQuery({ type, page: 1, per_page: 100 });

  const [createLabel, { isLoading: isCreating }] = useCreateLabelMutation();
  const [updateLabel, { isLoading: isUpdating }] = useUpdateLabelMutation();
  const [deleteLabel] = useDeleteLabelMutation();

  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [labelToDelete, setLabelToDelete] = useState<EntityLabel | null>(null);

  const confirmDelete = useBoolean();

  const results = useMemo(() => currentData?.results ?? [], [currentData]);
  const systemLabels = useMemo(
    () => results.filter((label) => label.scope_type === EntityLabelScopeType.SYSTEM),
    [results]
  );
  const tenantLabels = useMemo(
    () => results.filter((label) => label.scope_type === EntityLabelScopeType.TENANT),
    [results]
  );

  const handleCreate = async () => {
    const value = newLabel.trim();
    if (!value) return;

    try {
      await createLabel({ label: value, type, scope_type: EntityLabelScopeType.TENANT }).unwrap();
      setNewLabel('');
      enqueueSnackbar(t('admin-organization.settings.labels.api.create.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.labels.api.create.default_error'), { variant: 'error' });
    }
  };

  const handleStartEdit = (label: EntityLabel) => {
    setEditingId(label.id);
    setEditingValue(label.label ?? '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleSaveEdit = async () => {
    const value = editingValue.trim();
    if (!editingId || !value) return;

    try {
      await updateLabel({ id: editingId, label: value, type }).unwrap();
      handleCancelEdit();
      enqueueSnackbar(t('admin-organization.settings.labels.api.update.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.labels.api.update.default_error'), { variant: 'error' });
    }
  };

  const handleOpenDelete = (label: EntityLabel) => {
    setLabelToDelete(label);
    confirmDelete.onTrue();
  };

  const handleConfirmDelete = async () => {
    if (!labelToDelete) return;

    try {
      await deleteLabel({ id: labelToDelete.id, type }).unwrap();
      enqueueSnackbar(t('admin-organization.settings.labels.api.delete.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.labels.api.delete.default_error'), { variant: 'error' });
    } finally {
      confirmDelete.onFalse();
      setLabelToDelete(null);
    }
  };

  return (
    <Stack direction="column" gap={2}>
      <Typography variant="subtitle1">{title}</Typography>

      {isLoading ? (
        <Typography variant="body2" color="textSecondary">
          {t('common.loading')}
        </Typography>
      ) : (
        <>
          {/* Seeded system labels — shared across all agencies, read-only */}
          {systemLabels.length > 0 && (
            <Stack gap={1}>
              <Typography variant="caption" color="textSecondary">
                {t('admin-organization.settings.labels.system_labels')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {systemLabels.map((label) => (
                  <Chip key={label.id} label={label.label} size="small" variant="outlined" />
                ))}
              </Box>
            </Stack>
          )}

          {/* The agency's own labels — editable */}
          <Stack gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('admin-organization.settings.labels.your_labels')}
            </Typography>

            {tenantLabels.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                {t('admin-organization.settings.labels.no_custom_labels')}
              </Typography>
            )}

            {tenantLabels.map((label) => (
              <Stack key={label.id} direction="row" alignItems="center" gap={1}>
                {editingId === label.id ? (
                  <>
                    <TextField
                      value={editingValue}
                      onChange={(event) => setEditingValue(event.target.value)}
                      size="small"
                      fullWidth
                      autoFocus
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSaveEdit}
                      disabled={isUpdating || !editingValue.trim()}
                      aria-label={t('common.save')}
                    >
                      <Iconify icon="eva:checkmark-fill" />
                    </IconButton>
                    <IconButton onClick={handleCancelEdit} aria-label={t('common.cancel')}>
                      <Iconify icon="eva:close-fill" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {label.label}
                    </Typography>
                    <IconButton
                      onClick={() => handleStartEdit(label)}
                      disabled={!canEdit}
                      aria-label={t('common.edit')}
                    >
                      <Iconify icon="eva:edit-2-outline" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDelete(label)}
                      disabled={!canEdit}
                      aria-label={t('common.delete')}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </>
                )}
              </Stack>
            ))}

            {/* Add a new label */}
            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
              <TextField
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleCreate();
                  }
                }}
                placeholder={t('admin-organization.settings.labels.add_placeholder')}
                size="small"
                fullWidth
                disabled={!canEdit}
              />
              <LoadingButton
                variant="outlined"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleCreate}
                loading={isCreating}
                disabled={!canEdit || !newLabel.trim()}
              >
                {t('admin-organization.settings.labels.add')}
              </LoadingButton>
            </Stack>
          </Stack>
        </>
      )}

      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={t('admin-organization.settings.labels.delete_confirm.title')}
        content={t('admin-organization.settings.labels.delete_confirm.content', { label: labelToDelete?.label ?? '' })}
        action={
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            {t('common.delete')}
          </Button>
        }
      />
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function LabelSettingsCard() {
  const { t } = useTranslate();
  const theme = useTheme();

  const { account } = useAuthContext();

  const canEdit = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ width: '100%', position: 'relative', boxShadow: 0 }}>
        <CardHeader
          avatar={
            <Iconify
              icon="mdi:tag-multiple-outline"
              sx={{ width: 30, height: 30, color: theme.palette.primary.main }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.labels.title')}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="textSecondary">
              {t('admin-organization.settings.labels.description')}
            </Typography>
          }
        />

        <CardContent>
          <Stack direction="column" gap={3} sx={{ mt: 1 }}>
            <LabelTypeSection
              type={EntityLabelType.CANDIDATE}
              title={t('admin-organization.settings.labels.candidate_labels')}
              canEdit={canEdit}
            />

            <Divider />

            <LabelTypeSection
              type={EntityLabelType.COMPANY}
              title={t('admin-organization.settings.labels.company_labels')}
              canEdit={canEdit}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
